import React, { useContext, useState } from 'react'
import { StepperReturn } from 'selfServicePortal/components/Stepper/use-stepper'
import { Button } from 'selfServicePortal/components/ui/Button'
import { Card } from 'selfServicePortal/components/ui/Card'
import {
	FancyNumber,
	useFancyNumber,
	deriveFancyStep,
} from 'selfServicePortal/components/FancyNumber'
import { useTranslation } from 'react-i18next'
import { getCurrency } from 'shared'
import SspContext from 'selfServicePortal/contexts/SspContext'
import { useQuery } from '@tanstack/react-query'
import { getIbans } from 'selfServicePortal/services/CreditCardServices/getIbans'
import appConfig from 'app.config'
import { ICreditBalance } from 'selfServicePortal/interfaces/i-api-creaditCard'
import { Dropdown } from 'selfServicePortal/components/ui/Dropdown'
import { Input } from 'selfServicePortal/components/ui/Input'
import { getCountryA3 } from 'shared'
import { MessageCard } from 'selfServicePortal/components/Message/Message'
import { SpinnerCard } from 'selfServicePortal/components/ui/Spinner'
import { cashToAccountPreliminary } from 'selfServicePortal/services/CashToAccount/preliminary'
import { forceNavigateHome } from 'selfServicePortal/utils'

interface StepOneProps {
	stepper: StepperReturn
	dirtyData: {
		amount: number | undefined
		referenceAccountIban: string | undefined
	}
	onSubmit: (submitValues: {
		amount: number
		referenceAccountIban: string
		referenceAccountBic?: string | undefined
	}) => void
}

const minNumbers: Record<string, number> = {
	no: 200,
	de: 0,
	at: 0,
}

export function StepOne({ stepper, onSubmit, dirtyData }: StepOneProps) {
	const { customerData, overviewData } = useContext(SspContext)
	const { t } = useTranslation()

	const minNumber = minNumbers[appConfig.country]
	const maxNumber = Number(
		(overviewData as ICreditBalance)?.availableCreditAmount,
	)
	const fancyNumber = useFancyNumber({
		minNumber,
		maxNumber,
		step: deriveFancyStep(minNumber, maxNumber),
		initialNumber: dirtyData.amount ?? Math.round(maxNumber * 0.8),
	})

	const cashToAccountPreliminaryParams = {
		partyId: String(customerData?.customerId),
		accountAgreementId: String(overviewData?.accountAgreementId),
		market: getCountryA3(),
	}
	const cashToAccountPreliminaryMutation = useQuery({
		queryKey: [
			'cashToAccount',
			'preliminary',
			cashToAccountPreliminaryParams,
		],
		queryFn: () => cashToAccountPreliminary(cashToAccountPreliminaryParams),
	})

	const ibansQuery = useQuery({
		queryKey: ['ibans', { customerId: String(customerData?.customerId) }],
		queryFn: () => getIbans(String(customerData?.customerId)),
		enabled: appConfig.country === 'de' || appConfig.country === 'at',
	})

	const [iban, setIban] = useState<string | undefined>(
		dirtyData.referenceAccountIban,
	)
	const derivedBic = ibansQuery.data?.find(
		({ senderIban }) => senderIban === iban,
	)?.senderBic

	function handleSubmit() {
		if (iban) {
			onSubmit({
				amount: fancyNumber.number,
				referenceAccountIban: iban,
				referenceAccountBic: derivedBic,
			})
			stepper.nextStep()
		}
	}

	if (cashToAccountPreliminaryMutation.isLoading) {
		return <SpinnerCard />
	}

	if (cashToAccountPreliminaryMutation.isError) {
		return (
			<MessageCard
				type="fail"
				message={t('cashToAccountStepOneError')}
				footer={
					<Button variant="primary" onClick={forceNavigateHome}>
						{t('cashToAccountStepThreeButton')}
					</Button>
				}
			/>
		)
	}

	if (
		cashToAccountPreliminaryMutation.isSuccess &&
		cashToAccountPreliminaryMutation.data.state === 'Denied'
	) {
		return (
			<MessageCard
				type="fail"
				message={t(
					`CashToAccountResponses.error.${cashToAccountPreliminaryMutation.data.message}`,
				)}
				footer={
					<Button variant="primary" onClick={forceNavigateHome}>
						{t('cashToAccountStepThreeButton')}
					</Button>
				}
			/>
		)
	}

	return (
		<Card>
			<Card.Header>{t('cashToAccountStepOne')}</Card.Header>

			<Card.Content>
				<Card.InfoContainer>
					<Card.InfoTitle>
						{t('cashToAccountAccountMoneyTransferedTo')}
					</Card.InfoTitle>

					<Card.Info>
						<Card.InfoLabel>
							{t('cashToAccountAccountHolder')}
						</Card.InfoLabel>
						<Card.InfoValue>
							{`${customerData?.firstName} ${customerData?.lastName}`}
						</Card.InfoValue>
					</Card.Info>
					<Card.Info>
						<Card.InfoLabel>
							{t('cashToAccountAvailableLimit')}
						</Card.InfoLabel>
						<Card.InfoValue>
							{`${fancyNumber.maxNumber} ${getCurrency()}`}
						</Card.InfoValue>
					</Card.Info>
				</Card.InfoContainer>

				<FancyNumber>
					<FancyNumber.InputLabel>
						{t('cashToAccountRequestedAmount')}
					</FancyNumber.InputLabel>
					<FancyNumber.Input {...fancyNumber} />
					<FancyNumber.Slider {...fancyNumber} />
					<FancyNumber.SliderLabel
						left={`${fancyNumber.minNumber} ${getCurrency()}`}
						right={`${fancyNumber.maxNumber} ${getCurrency()}`}
					/>
				</FancyNumber>

				{ibansQuery.isSuccess && (
					<Dropdown.Container>
						<Dropdown.Label>
							{t('cashToAccountChooseAccount')}
						</Dropdown.Label>
						<Dropdown.Field
							value={iban}
							onChange={(e) => {
								setIban(e.target.value)
							}}>
							<Dropdown.Option
								hidden
								disabled
								selected></Dropdown.Option>
							{ibansQuery.data.map((value) => (
								<Dropdown.Option
									key={value.senderIban}
									value={value.senderIban}
									id={value.senderIban}>
									{value.senderIban}
								</Dropdown.Option>
							))}
						</Dropdown.Field>
						<Dropdown.Description>
							{t('cashToAccountInfoAboutInterest')}
						</Dropdown.Description>
					</Dropdown.Container>
				)}

				{appConfig.country === 'no' && (
					<>
						<Input.Container>
							<Input.Label>
								{t('cashToAccountChooseAccount')}
							</Input.Label>
							<Input.Field
								type="text"
								inputMode="numeric"
								maxLength={11}
								value={iban}
								onChange={(e) => {
									setIban(e.target.value.replace(/\D/, ''))
								}}
							/>
							<Input.Description className="interest-info-no">
								{t('cashToAccountInfoAboutInterest')}
							</Input.Description>
						</Input.Container>

						<p>{t('cashToAccountStepOneFooterInfo')}</p>
					</>
				)}
			</Card.Content>

			<Card.Footer>
				<Button
					variant="secondary"
					onClick={handleSubmit}
					disabled={!Boolean(iban)}>
					{t('cashToAccountNext')}
				</Button>
			</Card.Footer>
		</Card>
	)
}
